import React, { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import { usePublicProfile, useHeaderFooter, useCourseCatalogue } from '../../hooks';
import { Catalogue, SpinLoader } from '../../components';
import { AppDataContext } from '../../context';
import { getEnabledCourse } from '../../utils';

import './index.scss';
import messages from './messages';

import emailImg from '../../img/email.svg';
import linkedInImg from '../../img/linkedin.svg';
import xImg from '../../img/x-twitter.svg';
import graphicLImg from '../../img/profileGuide/graphic-l.svg';
import graphicRImg from '../../img/profileGuide/graphic-r.svg';
import line61Img from '../../img/profileGuide/line-6-1.svg';
import line73Img from '../../img/profileGuide/line-7-3.svg';

interface ProfileGuideProps {
  children?: React.ReactNode;
}

type UserNameMapping = {
  temmie: string;
  tadeyinka: string;
  tedward: string;
  tkaty: string;
  tgerald: string;
  tveronica: string;
  tjoann: string;
  tlina: string;
  tmatthew: string;
  tanjali: string;
  tluna: string;
};

const ProfileGuide: React.FC<ProfileGuideProps> = ({ children }) => {
  const { learnerID } = useParams<{ learnerID: string }>();
  const username = learnerID || '';
  const {
    headerContent,
    footerContent,
  } = useHeaderFooter(children);
  const { userInfo, userInfoLoaded } = usePublicProfile(learnerID);
  const intl = useIntl();

  const { allowedCourses } = useContext(AppDataContext);
  const [coursesData, courseDataLoaded] = useCourseCatalogue(allowedCourses);
  const enabledCourses = useMemo(
    () => getEnabledCourse(coursesData, allowedCourses, username),
    [coursesData, allowedCourses, username],
  );
  const hasCourses = courseDataLoaded && enabledCourses.length > 0;
  const profileUnavailable = userInfoLoaded && Object.keys(userInfo).length === 0;

  if (!userInfoLoaded || profileUnavailable) {
    const heroTitle = profileUnavailable
      ? intl.formatMessage(messages.profileNotFoundTitle)
      : '';

    return (
      <>
        <input
          type="hidden"
          id="anPageName"
          name="page"
          defaultValue="profile-guide"
        />
        <div className="container-center-horizontal">
          <div className="profile-guide screen profile-guide--not-found">
            <div className="overlap-group5">
              <div className="hero-image">
                <img className="graphic_r" src={graphicRImg} alt="Graphic_R" />
                <h1 className="guide-mike-smith recklessneue-book-normal-white-100-1px">
                  {heroTitle}
                </h1>
                <img className="graphic_l" src={graphicLImg} alt="Graphic_L" />
              </div>
              {headerContent}
            </div>
            <div className="profile-guide__not-found">
              {profileUnavailable
                ? <p>{intl.formatMessage(messages.profileNotFoundBody)}</p>
                : <SpinLoader size={40} color="#11385c" />}
            </div>
            {footerContent}
          </div>
        </div>
      </>
    );
  }

  const xLink = 'social_links' in userInfo
    && (
      userInfo.social_links?.find((link) => link?.platform === 'x')?.social_link
      || userInfo.social_links?.find((link) => link?.platform === 'twitter')?.social_link
    );
  const linkedInLink = 'social_links' in userInfo
    && userInfo.social_links?.find((link) => link?.platform === 'linkedin')
      ?.social_link;

  const userNameMapping: UserNameMapping = {
    temmie: 'Emmie Bidston',
    tadeyinka: 'Adeyinka Adewale',
    tedward: 'Edward Brooks',
    tkaty: 'Katy Granville-Chapman',
    tgerald: 'Gerald Chirinda',
    tveronica: 'Verónica Fernández',
    tjoann: 'JoAnn Flett',
    tlina: 'Lina Tori Jan',
    tmatthew: 'Matthew Lee',
    tanjali: 'Anjali Sarker',
    tluna: 'Luna Wei Wang',
  };

  const getDisplayName = () => {
    switch (true) {
      case ('name' in userInfo):
        return userInfo?.name;
      case username in userNameMapping:
        return userNameMapping[username as keyof UserNameMapping];
      default:
        return '';
    }
  };

  const presentedByLink = (chunks: React.ReactNode) => <a href="/presented-by">{chunks}</a>;

  return (
    <>
      <input
        type="hidden"
        id="anPageName"
        name="page"
        defaultValue="profile-guide"
      />
      <div className="container-center-horizontal">
        <div className="profile-guide screen">
          <div className="overlap-group5">
            <div className="hero-image">
              <img
                className="graphic_r"
                src={graphicRImg}
                alt="Graphic_R"
              />
              <h1 className="guide-mike-smith recklessneue-book-normal-white-100-1px">
                {intl.formatMessage(messages.guide)}
                <br />
                <span className="i-name">{getDisplayName()}</span>
              </h1>
              <img
                className="graphic_l"
                src={graphicLImg}
                alt="Graphic_L"
              />
            </div>
            {headerContent}
          </div>
          <div className="page-nav page">
            <p className="about-presented-by-mike-smith recklessneue-book-normal-congress-blue-22-8px">
              {intl.formatMessage(messages.aboutPresentedBy, {
                link: presentedByLink,
              })}
              <span className="i-name">{getDisplayName()}</span>
            </p>
            <div className="line-container">
              <img className="line-6" src={line61Img} alt="Line 6" />
              <img className="line-7" src={line73Img} alt="Line 7" />
            </div>
          </div>
          <div className={`mentor-bio${hasCourses ? '' : ' mentor-bio--no-courses'}`}>
            <div className="headshot">
              <img
                className="chris-blonk-bn5cob0k"
                src={`${
                  'profile_image' in userInfo
                    ? userInfo?.profile_image?.has_image
                    && userInfo?.profile_image?.image_url_full
                    : ''
                }`}
                alt=""
              />
            </div>
            <div className="overlap-group">
              <div className="dflex">
                <div className="guide-bio">
                  <span className="instrumentserif-normal-congress-blue-48px">
                    {intl.formatMessage(messages.guide)}{' '}
                  </span>
                  <span className="span1">{intl.formatMessage(messages.bio)}</span>
                </div>
                <div className="socials">
                  {'email' in userInfo && userInfo.email && (
                    <a
                      data-name="email"
                      href={`mailto:${userInfo.email}`}
                    >
                      <img className="email" src={emailImg} alt="Email" />
                    </a>
                  )}
                  {xLink && (
                    <a
                      data-name="x"
                      href={xLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        className="x"
                        src={xImg}
                        alt="X.com"
                      />
                    </a>
                  )}
                  {linkedInLink && (
                    <a
                      data-name="linkedin"
                      href={linkedInLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        className="linkedin"
                        src={linkedInImg}
                        alt="Linkedin"
                      />
                    </a>
                  )}
                </div>
              </div>
              <p className="lorem-ipsum-dolor-si neuehaasgroteskdisplaypro-45-light-congress-blue-24px c-bio">
                {'bio' in userInfo ? userInfo.bio : ''}
              </p>
            </div>
          </div>
          {hasCourses && (
            <div className="as-seen-on">
              <div className="providing-insighton-these-courses recklessneue-book-normal-congress-blue-48px">
                {intl.formatMessage(messages.providingInsight)}
                <br />
                {intl.formatMessage(messages.inTheseModules)}
              </div>
              <div className="overlap-group-container">
                <div className="courses-slider" data-name="courses-in-progress">
                  <Catalogue username={username} />
                </div>
              </div>
            </div>
          )}
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default ProfileGuide;
